'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

import { GA_ID } from '@/constants/common';
import { debounce } from '@/lib/utils';
import { reloadOnceForChunkError } from '@/lib/chunk-reload';
import {
  isIgnoredErrorMessage,
  isRecaptchaTeardownError
} from '@/lib/ignored-errors';
import {
  hasMarketingConsent,
  markEntryPageViewSent
} from '@/lib/consent/consent-manager';
import { sendMetaPageView } from '@/lib/meta/meta-pageview';
import { campaignFromParams } from '@/lib/posthog/utm-attribution';
import { scrubPii } from '@/lib/scrub-pii';
import {
  trackPageView,
  getPageTypeFromPath,
  initSessionTracking,
  setPreviousPage,
  setPreviousCategoryKey,
  clearPreviousPageContext,
  clearEntrySection,
  getEntrySection,
  setPageEntryOrigin,
  normalizeShopId,
  PageViewProperties
} from '@/lib/posthog/posthog-events';

const SCROLL_THRESHOLDS = [25, 50, 75, 100];
const INACTIVITY_THRESHOLD = 30 * 60 * 1000; // 30 minutes

const trackChurn = () => {
  window.gtag?.('event', 'user_inactivity', {
    event_category: 'User Behavior',
    event_label: 'User Inactive for 30 minutes'
  });
};

export const usePageView = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const previousFullPathRef = useRef<string | null>(null);

  // One-time global error listeners (not tied to route changes)
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (reloadOnceForChunkError(event.error)) return;
      if (
        isIgnoredErrorMessage(event.message) ||
        isRecaptchaTeardownError(event.message)
      )
        return;
      window.gtag?.('event', 'exception', {
        description: `${event.message} at ${event.filename}:${event.lineno}`,
        fatal: true
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (reloadOnceForChunkError(event.reason)) return;
      if (isIgnoredErrorMessage(event.reason?.message)) return;
      window.gtag?.('event', 'exception', {
        description: event.reason?.message || 'Unhandled Rejection',
        fatal: true
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  useEffect(() => {
    const queryString = searchParams?.toString();
    const fullUrl = queryString ? `${pathname}?${queryString}` : pathname;

    const scrubbedUrl = scrubPii(fullUrl);

    if (window?.gtag && GA_ID) {
      window.gtag('config', GA_ID, {
        page_path: scrubbedUrl,
        page_location: `${window.location.origin}${scrubbedUrl}`,
        ...(document.referrer
          ? { page_referrer: scrubPii(document.referrer) }
          : {})
      });
    }

    if (hasMarketingConsent()) {
      markEntryPageViewSent();
      sendMetaPageView(`${window.location.origin}${scrubbedUrl}`);
    }

    const pageType = getPageTypeFromPath(pathname);
    initSessionTracking(
      pageType,
      campaignFromParams((key) => searchParams?.get(key))
    );

    const pageViewProps: PageViewProperties = { page_type: pageType };

    if (pathname.startsWith('/category/')) {
      pageViewProps.category_key = pathname.split('/')[2];
    } else if (pathname.startsWith('/subcategory/')) {
      pageViewProps.category_key = pathname.split('/')[2];
    } else if (pathname.startsWith('/retailer/')) {
      pageViewProps.shop_id = normalizeShopId(pathname.split('/')[2]);
    } else if (pathname.startsWith('/deal/')) {
      pageViewProps.offer_id = pathname.split('/')[2];
    }

    // Attach previous page context (undefined on first page load)
    if (previousFullPathRef.current) {
      const prevFullPath = previousFullPathRef.current;
      const prevPath = prevFullPath.split('?')[0];
      const prevPageType = getPageTypeFromPath(prevPath);
      const prevUrl = `${window.location.origin}${scrubPii(prevFullPath)}`;
      setPreviousPage(prevPageType, prevUrl);
      pageViewProps.previous_page_type = prevPageType;
      pageViewProps.previous_url = prevUrl;

      let prevCategoryKey: string | undefined;
      if (prevPath.startsWith('/category/'))
        prevCategoryKey = prevPath.split('/')[2];
      else if (prevPath.startsWith('/subcategory/'))
        prevCategoryKey = prevPath.split('/')[2];
      setPreviousCategoryKey(prevCategoryKey);
    } else {
      // First page load — clear stale previous-page values persisted in PostHog localStorage
      clearPreviousPageContext();
    }

    previousFullPathRef.current = fullUrl;

    trackPageView(pageViewProps);
    // Stash this page's entry (e.g. top_8_widget) for voucher-click attribution, then clear so it can't leak to later page_views.
    setPageEntryOrigin(getEntrySection());
    clearEntrySection();

    const handleExitIntent = (event: MouseEvent) => {
      if (event.clientY <= 0) {
        window.gtag?.('event', 'exit_intent', {
          event_category: 'User Interaction',
          event_label: 'Mouse left viewport'
        });
      }
    };

    // Scroll Depth Tracking - INP optimized with cached DOM reads
    const trackedDepths = new Set<number>();
    let cachedScrollHeight = document.body.scrollHeight;
    let lastScrollHeightUpdate = Date.now();

    const handleScroll = debounce(() => {
      const now = Date.now();
      if (now - lastScrollHeightUpdate > 1000) {
        cachedScrollHeight = document.body.scrollHeight;
        lastScrollHeightUpdate = now;
      }

      const scrolledPercentage = Math.round(
        ((window.scrollY + window.innerHeight) / cachedScrollHeight) * 100
      );

      for (const threshold of SCROLL_THRESHOLDS) {
        if (scrolledPercentage >= threshold && !trackedDepths.has(threshold)) {
          window.gtag?.('event', 'scroll_depth', {
            event_category: 'User Interaction',
            event_label: `${threshold}%`,
            value: threshold
          });
          trackedDepths.add(threshold);
        }
      }
    }, 200);

    // Inactivity Tracking - INP optimized with debounced reset
    let inactivityTimeout: ReturnType<typeof setTimeout>;
    let lastActivityTime = Date.now();

    const resetInactivityTimer = debounce(() => {
      const now = Date.now();
      if (now - lastActivityTime > 1000) {
        lastActivityTime = now;
        clearTimeout(inactivityTimeout);
        inactivityTimeout = setTimeout(trackChurn, INACTIVITY_THRESHOLD);
      }
    }, 1000);

    document.addEventListener('mouseleave', handleExitIntent);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', resetInactivityTimer, {
      passive: true
    });
    window.addEventListener('keydown', resetInactivityTimer, { passive: true });

    inactivityTimeout = setTimeout(trackChurn, INACTIVITY_THRESHOLD);

    return () => {
      document.removeEventListener('mouseleave', handleExitIntent);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', resetInactivityTimer);
      window.removeEventListener('keydown', resetInactivityTimer);
      handleScroll.cancel();
      resetInactivityTimer.cancel();
      clearTimeout(inactivityTimeout);
    };
  }, [pathname, searchParams]);
};
